"use client";

import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  fetchNamespaceRepositories,
  fetchNamespaceTask,
  fetchNamespaceTasks,
  type NamespaceTasksResponse,
  type NamespaceTaskListQuery,
} from "@/lib/api-client";
import type { TaskSummary } from "@/types/tasks";
import { queryKeys } from "@/lib/query/keys";
import type { NamespaceId } from "@/types/namespaces";
import type { WorkspaceRepository } from "@/types/workspaces";

const DISABLED_NAMESPACE_ID = '__namespace-disabled__' as NamespaceId;

export function useNamespaceTasksQuery(
  namespaceId?: NamespaceId,
  queryParams?: NamespaceTaskListQuery,
) {
  const queryKeyNamespace = namespaceId ?? DISABLED_NAMESPACE_ID;
  return useQuery<NamespaceTasksResponse>({
    queryKey: queryKeys.namespaceTasks(queryKeyNamespace, {
      state: queryParams?.state ?? null,
      search: queryParams?.search ?? null,
      sort: queryParams?.sort ?? null,
      createdFrom: queryParams?.createdFrom ?? null,
      createdTo: queryParams?.createdTo ?? null,
      page: queryParams?.page ?? null,
      pageSize: queryParams?.pageSize ?? null,
    }),
    queryFn: () => {
      if (!namespaceId) {
        throw new Error("Namespace ID is required to load tasks.");
      }
      return fetchNamespaceTasks(namespaceId, queryParams);
    },
    enabled: Boolean(namespaceId),
    staleTime: 60_000,
    // Keep the previous page's rows on screen while the next page/filter
    // combination loads, so paging doesn't flash an empty list.
    placeholderData: keepPreviousData,
  });
}

export function useNamespaceTaskQuery(
  namespaceId: NamespaceId | undefined,
  taskNumber: number | null,
  options?: { enabled?: boolean },
) {
  const enabledFlag = options?.enabled ?? true;
  const numericTaskNumber = typeof taskNumber === "number" && Number.isFinite(taskNumber)
    ? taskNumber
    : null;
  const queryKeyNamespace = namespaceId ?? DISABLED_NAMESPACE_ID;
  return useQuery<TaskSummary>({
    queryKey: queryKeys.namespaceTask(queryKeyNamespace, numericTaskNumber ?? -1),
    queryFn: () => {
      if (!namespaceId) {
        throw new Error("Namespace ID is required to load a task.");
      }
      if (numericTaskNumber === null) {
        throw new Error("A numeric task identifier is required.");
      }
      return fetchNamespaceTask(namespaceId, numericTaskNumber);
    },
    enabled: enabledFlag && Boolean(namespaceId) && numericTaskNumber !== null,
    staleTime: 60_000,
  });
}

export function useNamespaceRepositoriesQuery(namespaceId?: NamespaceId) {
  return useQuery<WorkspaceRepository[]>({
    queryKey: queryKeys.namespaceRepositories(namespaceId ?? DISABLED_NAMESPACE_ID),
    queryFn: () => {
      if (!namespaceId) {
        throw new Error("Namespace ID is required to load repositories.");
      }
      return fetchNamespaceRepositories(namespaceId);
    },
    enabled: Boolean(namespaceId),
    staleTime: 60_000,
  });
}
